import React from 'react';

/* Shared layout for the two pages that render a questionnaire for answering: the tenant-side
   Questionnaire Library preview (QuestionnairePreview.tsx) and the vendor portal's real answering
   page (../QuestionnaireResponse/AnswerQuestionnaire.tsx). Each page keeps its own data loading and
   saving; this file owns the follow-up (dependent question) logic and the shared look -- coloured
   section sidebar, one section at a time, compact answer pills.

   Follow-up logic is unchanged from the two pages' previous copies: a hidden question nests under
   its trigger (from depends_on) and only exists in the tree once that trigger is answered "Yes",
   chaining as many levels deep as the questionnaire defines. The sidebar additionally lists the
   not-yet-revealed follow-ups of each visible question as locked rows, so the total never looks
   wrong -- that's display only, the tree itself (and every count) is exactly as before. */

export interface QItem {
  id: string;
  question_key: string;
  question_text: string;
  question_type: string | null;
  visibility: 'visible' | 'hidden';
  answer_selections: string | null;
  section: string | null;
  depends_on: string | null;
}

export interface TreeNode<T extends QItem = QItem> { item: T; children: TreeNode<T>[] }
export interface FlatRow<T extends QItem = QItem> { item: T; depth: number; parentKey: string | null }

export const parseSelections = (raw: string | null) =>
  (raw || '').split('\n').map(s => s.replace(/^[•\-*]\s*/, '').trim()).filter(Boolean);

/* ─── Follow-up (dependent question) rule ───────────────────────────────
   Must stay identical to backend/services/questionnaire_visibility.py, which the server uses for the
   submit check and for every progress count -- if the two disagree, a vendor could be asked to answer a
   question they never saw. The library data only records which question a follow-up depends on, not which
   answer reveals it, so the rule follows the trigger's own option wording:
     - trigger offers a Yes-style option ("Yes", "Yes, we have firewalls ...")  -> answer starts with "Yes";
     - follow-up is an "Other, please specify" question (its text says "other" and the trigger offers
       an "Other..." option)                                                    -> an "Other..." option picked;
     - any other trigger (select-all checkbox, frequency pick, free text, upload) -> any answer.  */

export const isYes = (value: string | undefined) => !!value && /^yes\b/i.test(value.trim());

export const hasYesOption = (item: QItem) => parseSelections(item.answer_selections).some(o => /^yes\b/i.test(o));

export const isOtherFollowUp = (trigger: QItem, child: QItem) =>
  /\bother\b/i.test(child.question_text || '') && parseSelections(trigger.answer_selections).some(o => /^other\b/i.test(o));

export const revealsFollowUp = (trigger: QItem, child: QItem, value: string | undefined): boolean => {
  if (hasYesOption(trigger)) return isYes(value);
  if (!(value || '').trim()) return false;
  if (isOtherFollowUp(trigger, child)) return value!.split('\n').some(line => /^other\b/i.test(line.trim()));
  return true;
};

// What the sidebar says about a follow-up that isn't showing yet.
export const followUpHint = (trigger: QItem, child: QItem) => {
  const code = splitLeadingCode(trigger.question_text).code || 'the question above';
  if (hasYesOption(trigger)) return `Shown if ${code} is Yes`;
  if (isOtherFollowUp(trigger, child)) return `Shown if "Other" is picked in ${code}`;
  return `Shown once ${code} is answered`;
};

// A "No" / "Not Applicable" style answer is the only case a reason note is relevant to. Kept in
// sync with VendorQuestionnaires.tsx and backend/services/questionnaire_signals.py.
export const isNegativeAnswer = (value: string | null | undefined): boolean => {
  const v = (value || '').trim();
  if (!v) return false;
  return /^no\b/i.test(v) || /not applicable/i.test(v) || /^n\/a\.?$/i.test(v);
};

// depends_on is "{trigger_question_key} ({trigger_number})", or just "{trigger_question_key}" when
// the trigger has no number of its own -- either way the key is the text before the first " (".
export const triggerKeyOf = (dependsOn: string | null) => {
  if (!dependsOn) return null;
  const idx = dependsOn.indexOf(' (');
  return idx >= 0 ? dependsOn.slice(0, idx) : dependsOn;
};

// A question's own leading code ("1.2.1.3", "GNRL-01", "E1") pulled off the front of its text --
// only when that token contains a digit, so a sentence-starting word is never mistaken for one.
export const splitLeadingCode = (text: string): { code: string | null; rest: string } => {
  const m = text.match(/^\s*([A-Za-z0-9]+(?:[.\-][A-Za-z0-9]+)*)\.?\s+(.*)$/s);
  if (m && /\d/.test(m[1])) return { code: m[1], rest: m[2] };
  return { code: null, rest: text };
};

export function hiddenChildrenOf<T extends QItem>(items: T[]): Map<string, T[]> {
  const childrenOf = new Map<string, T[]>();
  for (const item of items) {
    if (item.visibility !== 'hidden') continue;
    const trigger = triggerKeyOf(item.depends_on);
    if (!trigger) continue;
    if (!childrenOf.has(trigger)) childrenOf.set(trigger, []);
    childrenOf.get(trigger)!.push(item);
  }
  return childrenOf;
}

// One tree per section. A showing follow-up nests under its trigger when both are in the same section;
// a follow-up that belongs to a later section (some questionnaires chain whole sections this way) shows as
// a normal question in its own section instead of being pulled into the trigger's. Every section of the
// questionnaire is returned, in order -- one whose questions are all still hidden comes back with no
// nodes, so the sidebar can list it as "not shown yet" rather than the section silently not existing.
export function buildSections<T extends QItem>(items: T[], answers: Record<string, string>) {
  const childrenOf = hiddenChildrenOf(items);
  const byKey = new Map(items.map(i => [i.question_key, i]));
  const sectionOf = (i: T) => i.section || 'Other';
  const triggerOf = (i: T) => byKey.get(triggerKeyOf(i.depends_on) || '');

  const memo = new Map<string, boolean>();
  const shown = (item: T, depth = 0): boolean => {
    const known = memo.get(item.id);
    if (known !== undefined) return known;
    let result = true;
    if (item.visibility === 'hidden') {
      const trigger = triggerOf(item);
      result = !!trigger && depth < 50 && trigger.id !== item.id && shown(trigger, depth + 1)
        && revealsFollowUp(trigger, item, answers[trigger.question_key]);
    }
    memo.set(item.id, result);
    return result;
  };

  const buildNode = (item: T): TreeNode<T> => ({
    item,
    children: (childrenOf.get(item.question_key) || [])
      .filter(child => sectionOf(child) === sectionOf(item) && shown(child))
      .map(buildNode),
  });

  const order: string[] = [];
  const bySection = new Map<string, TreeNode<T>[]>();
  for (const item of items) {
    const key = sectionOf(item);
    if (!bySection.has(key)) { bySection.set(key, []); order.push(key); }
    if (!shown(item)) continue;
    if (item.visibility === 'hidden') {
      const trigger = triggerOf(item);
      if (trigger && sectionOf(trigger) === key) continue; // nested under its trigger
    }
    bySection.get(key)!.push(buildNode(item));
  }
  return order.map(key => ({ key, nodes: bySection.get(key)! }));
}

export function flattenNodes<T extends QItem>(nodes: TreeNode<T>[], depth = 0, parentKey: string | null = null): FlatRow<T>[] {
  return nodes.flatMap(n => [{ item: n.item, depth, parentKey }, ...flattenNodes(n.children, depth + 1, n.item.question_key)]);
}

export const codeOf = (item: QItem) => splitLeadingCode(item.question_text).code;

/* ─── Colours ───────────────────────────────────────────────────────── */
// Colour carries meaning, never decoration: green answered, blue current, amber "No" without a
// reason, red reviewer finding, purple follow-up, grey not started. No tinted cards or edge strips --
// tints only appear on small chips and on the selected section (the main sidebar's soft-pill style).

export interface QPalette {
  ink: string; ink2: string; muted: string; faint: string; line: string; lineStrong: string;
  bg: string; surface: string; surface2: string; hover: string;
  blue: string; blueBg: string; blueInk: string;
  green: string; greenBg: string; amber: string; amberBg: string; amberBdr: string;
  red: string; redBg: string; purple: string; purpleBg: string; teal: string; tealBg: string;
}

export const qPalette = (isDark: boolean): QPalette => isDark ? {
  ink: '#E2E8F0', ink2: '#CBD5E1', muted: '#94A3B8', faint: '#64748B', line: '#242424', lineStrong: '#3a3a3a',
  bg: '#000000', surface: '#0D0D0D', surface2: '#141414', hover: '#1A1A1A',
  blue: '#3B82F6', blueBg: '#0C2A4A', blueInk: '#DBEAFE',
  green: '#34D399', greenBg: 'rgba(52,211,153,0.14)', amber: '#FBBF24', amberBg: 'rgba(251,191,36,0.12)', amberBdr: '#B45309',
  red: '#F87171', redBg: 'rgba(248,113,113,0.14)', purple: '#C4B5FD', purpleBg: 'rgba(139,92,246,0.18)',
  teal: '#5EEAD4', tealBg: 'rgba(45,212,191,0.14)',
} : {
  ink: '#0F172A', ink2: '#1E293B', muted: '#64748B', faint: '#94A3B8', line: '#E2E8F0', lineStrong: '#CBD5E1',
  bg: '#F5F6FA', surface: '#FFFFFF', surface2: '#F8FAFC', hover: '#F1F5F9',
  blue: '#2563EB', blueBg: '#E6F1FB', blueInk: '#0C447C',
  green: '#15803D', greenBg: '#ECFDF5', amber: '#B45309', amberBg: '#FFFBEB', amberBdr: '#F59E0B',
  red: '#DC2626', redBg: '#FEF2F2', purple: '#6D28D9', purpleBg: '#F3E8FF', teal: '#0F766E', tealBg: '#F0FDFA',
};

export type QStatus = 'answered' | 'needs-note' | 'finding' | 'unanswered';
export type ChipTone = 'blue' | 'slate' | 'amber' | 'red' | 'purple' | 'green' | 'teal';

const toneColors = (p: QPalette, tone: ChipTone) => ({
  blue: { fg: p.blueInk, bg: p.blueBg, solid: p.blue },
  slate: { fg: p.muted, bg: p.surface2, solid: p.ink2 },
  amber: { fg: p.amber, bg: p.amberBg, solid: p.amber },
  red: { fg: p.red, bg: p.redBg, solid: p.red },
  purple: { fg: p.purple, bg: p.purpleBg, solid: p.purple },
  green: { fg: p.green, bg: p.greenBg, solid: p.green },
  teal: { fg: p.teal, bg: p.tealBg, solid: p.teal },
}[tone]);

export function Chip({ p, tone, children, title }: { p: QPalette; tone: ChipTone; children: React.ReactNode; title?: string }) {
  const c = toneColors(p, tone);
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99, fontSize: 11,
      fontWeight: 600, background: c.bg, color: c.fg, whiteSpace: 'nowrap', flexShrink: 0,
    }}>{children}</span>
  );
}

const LockIcon = ({ color }: { color: string }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const CheckCircle = ({ color }: { color: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
);
const Chevron = ({ color, open }: { color: string; open: boolean }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .12s' }}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);
export const FollowUpArrow = ({ color }: { color: string }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M6 4v9a3 3 0 0 0 3 3h10" /><path d="M15 12l4 4-4 4" />
  </svg>
);

/* ─── Sidebar ───────────────────────────────────────────────────────── */

export interface SidebarFilter { key: string; label: string; count: number; tone: ChipTone }

interface SidebarProps<T extends QItem> {
  p: QPalette;
  font: string;
  top: number;
  sections: { key: string; nodes: TreeNode<T>[] }[];
  sectionTitle: (key: string) => string;
  hidden: Map<string, T[]>;
  statusOf: (item: T) => QStatus;
  activeSection: string | null;
  onSelectSection: (key: string) => void;
  activeKey: string | null;
  onJump: (key: string) => void;
  search: string;
  onSearch: (value: string) => void;
  filters: SidebarFilter[];
  filter: string;
  onFilter: (key: string) => void;
  filtered: FlatRow<T>[] | null;
  answeredCount: number;
  totalCount: number;
  subtitle?: string;
  showFindingLegend?: boolean;
  // Section done-counts; defaults to "has any status but unanswered".
  isAnswered?: (item: T) => boolean;
}

export function QuestionnaireSidebar<T extends QItem>(props: SidebarProps<T>) {
  const {
    p, font, top, sections, sectionTitle, hidden, statusOf, activeSection, onSelectSection, activeKey, onJump,
    search, onSearch, filters, filter, onFilter, filtered, answeredCount, totalCount, subtitle, showFindingLegend,
  } = props;
  const answered = props.isAnswered || ((item: T) => statusOf(item) !== 'unanswered');
  const pct = totalCount ? Math.round((answeredCount / totalCount) * 100) : 0;

  const dotFor = (item: T): React.ReactNode => {
    if (activeKey === item.question_key) return <span style={dot(p.blue)} />;
    const s = statusOf(item);
    if (s === 'answered') return <span style={dot(p.green)} />;
    if (s === 'needs-note') return <span style={dot(p.amber)} />;
    if (s === 'finding') return <span style={dot(p.red)} />;
    return <span style={{ ...dot('transparent'), border: `1.5px solid ${p.faint}` }} />;
  };

  const questionRow = (item: T, depth: number, followUps: number) => {
    const active = activeKey === item.question_key;
    const { code, rest } = splitLeadingCode(item.question_text);
    const status = statusOf(item);
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onJump(item.question_key)}
        title={item.question_text}
        style={{
          display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', fontFamily: font, cursor: 'pointer',
          padding: `6px 8px 6px ${8 + depth * 18}px`, fontSize: 12.5, borderRadius: 7, border: 'none', marginBottom: 1,
          background: active ? p.hover : 'none', color: p.ink,
        }}
      >
        {depth > 0 && <FollowUpArrow color={p.purple} />}
        {dotFor(item)}
        {code && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: p.muted, flexShrink: 0 }}>{code}</span>}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: active ? 600 : 400 }}>{rest}</span>
        {status === 'needs-note' && <Chip p={p} tone="amber">add reason</Chip>}
        {status === 'finding' && <Chip p={p} tone="red">finding</Chip>}
        {followUps > 0 && status !== 'needs-note' && status !== 'finding' && (
          <Chip p={p} tone="purple" title={`${followUps} follow-up question${followUps === 1 ? '' : 's'}`}>+{followUps}</Chip>
        )}
      </button>
    );
  };

  const lockedRow = (child: T, parent: T, depth: number) => {
    const childCode = codeOf(child);
    const hint = followUpHint(parent, child);
    return (
      <button
        key={`locked-${child.id}`}
        type="button"
        onClick={() => onJump(parent.question_key)}
        title={hint}
        style={{
          display: 'flex', alignItems: 'center', gap: 7, width: '100%', textAlign: 'left', fontFamily: font, cursor: 'pointer',
          padding: `5px 8px 5px ${8 + depth * 18}px`, fontSize: 12, borderRadius: 7, border: 'none', marginBottom: 1,
          background: 'none', color: p.faint,
        }}
      >
        <LockIcon color={p.faint} />
        {childCode && <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, flexShrink: 0 }}>{childCode}</span>}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
          {hint}
        </span>
      </button>
    );
  };

  // Each follow-up of a question, in order: the real (showing) one, or a locked row saying what reveals it.
  // Follow-ups that belong to another section are left to that section.
  const renderNodes = (nodes: TreeNode<T>[], depth: number): React.ReactNode[] => nodes.flatMap(node => {
    const section = node.item.section || 'Other';
    const kids = (hidden.get(node.item.question_key) || []).filter(k => (k.section || 'Other') === section);
    const showing = new Map(node.children.map(c => [c.item.question_key, c]));
    const out: React.ReactNode[] = [questionRow(node.item, depth, kids.length)];
    kids.forEach(k => {
      const child = showing.get(k.question_key);
      if (child) out.push(...renderNodes([child], depth + 1));
      else out.push(lockedRow(k, node.item, depth + 1));
    });
    return out;
  });

  return (
    <aside style={{
      width: 380, flexShrink: 0, height: `calc(100vh - ${top}px)`, position: 'sticky', top, overflowY: 'auto',
      background: p.surface, borderRight: `1px solid ${p.line}`, fontFamily: font,
    }}>
      <div style={{ padding: '18px 16px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `conic-gradient(${pct === 100 ? p.green : p.blue} 0 ${pct}%, ${p.hover} ${pct}% 100%)`,
        }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: p.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: p.ink }}>
            {pct}%
          </div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: p.ink }}>{answeredCount} of {totalCount} answered</div>
          {subtitle && <div style={{ fontSize: 12, color: p.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>

      <div style={{ padding: '0 16px 12px', borderBottom: `1px solid ${p.line}` }}>
        <input
          value={search}
          onChange={e => onSearch(e.target.value)}
          placeholder="Search questions"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '8px 11px', fontSize: 12.5, fontFamily: font, color: p.ink,
            background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 8, outline: 'none', marginBottom: 10,
          }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {filters.map(f => {
            const c = toneColors(p, f.tone);
            const on = filter === f.key;
            return (
              <button key={f.key} type="button" onClick={() => onFilter(f.key)} style={{
                padding: '4px 10px', borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 11.5, fontWeight: 600,
                background: on ? c.solid : c.bg, color: on ? '#fff' : c.fg, whiteSpace: 'nowrap',
              }}>
                {f.label} {f.count}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: '10px 8px 18px' }}>
        {filtered ? (
          filtered.length ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: p.faint, padding: '2px 8px 6px', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {filtered.length} match{filtered.length === 1 ? '' : 'es'}
              </div>
              {filtered.map(r => questionRow(r.item, r.depth ? 1 : 0, 0))}
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: p.muted, padding: '12px 8px' }}>
              No questions match {search.trim() ? `"${search}"` : 'this filter'}.
            </div>
          )
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: p.faint, padding: '2px 8px 6px', textTransform: 'uppercase', letterSpacing: '.04em' }}>Sections</div>
            {sections.map((s, idx) => {
              const flat = flattenNodes(s.nodes);
              const done = flat.filter(r => answered(r.item)).length;
              const complete = flat.length > 0 && done === flat.length;
              const active = s.key === activeSection;
              const sectionPct = flat.length ? Math.round((done / flat.length) * 100) : 0;
              const numTone: ChipTone = complete ? 'green' : active ? 'blue' : 'slate';
              const nc = toneColors(p, numTone);
              return (
                <div key={s.key} style={{ marginBottom: 2 }}>
                  <button type="button" onClick={() => onSelectSection(s.key)} style={{
                    width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', fontFamily: font, borderRadius: 8,
                    padding: '8px 8px 9px', background: active ? p.blueBg : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        minWidth: 22, height: 22, padding: '0 5px', boxSizing: 'border-box', borderRadius: 6, fontSize: 11.5, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: active ? p.blue : nc.bg, color: active ? '#fff' : nc.fg,
                      }}>{s.key.length <= 3 ? s.key : idx + 1}</span>
                      <span style={{
                        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13,
                        fontWeight: active ? 700 : 500, color: active ? p.blueInk : p.ink,
                      }}>{sectionTitle(s.key)}</span>
                      {complete && <CheckCircle color={p.green} />}
                      {flat.length === 0 ? (
                        <span title="This section's questions appear once you answer earlier ones" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: p.faint }}>
                          <LockIcon color={p.faint} /> not shown yet
                        </span>
                      ) : (
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: complete ? p.green : active ? p.blueInk : p.muted, fontVariantNumeric: 'tabular-nums' }}>
                          {done}/{flat.length}
                        </span>
                      )}
                      <Chevron color={active ? p.blueInk : p.faint} open={active} />
                    </div>
                    <div style={{ height: 4, borderRadius: 99, background: active ? p.surface : p.hover, marginTop: 7, marginLeft: 30, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${sectionPct}%`, borderRadius: 99, background: complete ? p.green : p.blue, transition: 'width .2s' }} />
                    </div>
                  </button>
                  {active && s.nodes.length > 0 && <div style={{ padding: '4px 0 8px 6px' }}>{renderNodes(s.nodes, 0)}</div>}
                </div>
              );
            })}
          </>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', padding: '14px 8px 0', marginTop: 10, borderTop: `1px solid ${p.line}`, fontSize: 11, color: p.muted }}>
          <Legend dotStyle={dot(p.green)} label="Answered" />
          <Legend dotStyle={dot(p.blue)} label="Current" />
          <Legend dotStyle={dot(p.amber)} label="No reason yet" />
          {showFindingLegend && <Legend dotStyle={dot(p.red)} label="Reviewer finding" />}
          <Legend dotStyle={{ ...dot('transparent'), border: `1.5px solid ${p.faint}` }} label="Unanswered" />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FollowUpArrow color={p.purple} /> Follow-up</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><LockIcon color={p.faint} /> Not shown yet</span>
        </div>
      </div>
    </aside>
  );
}

const dot = (bg: string): React.CSSProperties => ({ width: 8, height: 8, borderRadius: '50%', background: bg, flexShrink: 0, boxSizing: 'border-box', display: 'inline-block' });

function Legend({ dotStyle, label }: { dotStyle: React.CSSProperties; label: string }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={dotStyle} />{label}</span>;
}

/* ─── Question card pieces ──────────────────────────────────────────── */

// A follow-up sits indented under its parent with a purple "Follow-up to X" label -- the
// relationship is shown by the label and indent, not a coloured edge strip.
export function QuestionFrame({ p, font, item, depth, parentCode, active, needsNote, setRef, onFocus, badges, children }: {
  p: QPalette; font: string; item: QItem; depth: number; parentCode: string | null; active: boolean; needsNote: boolean;
  setRef: (el: HTMLDivElement | null) => void; onFocus: () => void; badges?: React.ReactNode; children: React.ReactNode;
}) {
  const { code, rest } = splitLeadingCode(item.question_text);
  return (
    <div
      ref={setRef}
      onFocus={onFocus}
      style={{
        marginLeft: depth * 28, background: p.surface, borderRadius: 12, padding: '18px 22px', marginBottom: 12,
        border: `1px solid ${active ? p.blue : needsNote ? p.amberBdr : p.line}`,
        boxShadow: active ? `0 0 0 3px ${p.blueBg}` : 'none', scrollMarginTop: 120, fontFamily: font,
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      {depth > 0 && (
        <div style={{ marginBottom: 10 }}>
          <Chip p={p} tone="purple"><FollowUpArrow color={p.purple} /> Follow-up to {parentCode || 'the question above'}</Chip>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
        {code && (
          <span style={{
            flexShrink: 0, marginTop: 1, padding: '3px 8px', borderRadius: 6, fontSize: 11.5, fontWeight: 600,
            fontFamily: 'ui-monospace, monospace', background: p.surface2, border: `1px solid ${p.line}`, color: p.muted,
          }}>{code}</span>
        )}
        <div style={{ fontSize: 14.5, lineHeight: 1.6, color: p.ink, fontWeight: 500 }}>{rest}</div>
      </div>
      {badges && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>{badges}</div>}
      {children}
    </div>
  );
}

const letterOf = (i: number) => String.fromCharCode(97 + i);

// Short option sets (Yes / No / Not applicable and similar) render as one row of pills; anything
// longer keeps the vertical list so a long option is never cramped into a pill.
export function AnswerControl({ p, font, item, value, onChange, onFocus }: {
  p: QPalette; font: string; item: QItem; value: string; onChange: (v: string) => void; onFocus: () => void;
}) {
  const isRadio = item.question_type === 'radio';
  const isCheckbox = item.question_type === 'checkbox';
  const isText = item.question_type === 'text';
  const options = (isRadio || isCheckbox) ? parseSelections(item.answer_selections) : [];
  const compact = isRadio && options.length > 0 && options.length <= 4 && options.every(o => o.length <= 30);

  const checkedOptions = isCheckbox ? new Set(value.split('\n').map(s => s.trim()).filter(Boolean)) : null;
  const toggleOption = (opt: string) => {
    if (!checkedOptions) return;
    const next = new Set(checkedOptions);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(Array.from(next).join('\n'));
  };

  if (compact) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map(opt => {
          const selected = value === opt;
          return (
            <button key={opt} type="button" onClick={() => onChange(opt)} onFocus={onFocus} style={{
              padding: '8px 18px', borderRadius: 99, fontSize: 13, fontFamily: font, cursor: 'pointer', fontWeight: selected ? 700 : 500,
              border: `1px solid ${selected ? p.blue : p.lineStrong}`, background: selected ? p.blue : p.surface,
              color: selected ? '#fff' : p.ink, transition: 'background .1s, border-color .1s',
            }}>{opt}</button>
          );
        })}
      </div>
    );
  }

  if (isRadio || isCheckbox) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {options.map((opt, i) => {
          const selected = isRadio ? value === opt : checkedOptions!.has(opt);
          return (
            <button key={opt} type="button" onClick={() => (isRadio ? onChange(opt) : toggleOption(opt))} onFocus={onFocus} style={{
              display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left', fontSize: 13, fontFamily: font,
              cursor: 'pointer', padding: '10px 14px', borderRadius: 8, fontWeight: selected ? 600 : 400,
              border: `1px solid ${selected ? p.blue : p.line}`, background: selected ? p.blueBg : p.surface,
              color: selected ? p.blueInk : p.ink, transition: 'background .1s, border-color .1s',
            }}>
              <span style={{
                flexShrink: 0, width: 16, height: 16, borderRadius: isRadio ? '50%' : 4, boxSizing: 'border-box',
                border: `1.5px solid ${selected ? p.blue : p.faint}`, background: selected && isCheckbox ? p.blue : 'none',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {selected && isRadio && <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.blue }} />}
                {selected && isCheckbox && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                )}
              </span>
              <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: p.faint, width: 12 }}>{letterOf(i)}</span>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      onFocus={onFocus}
      placeholder={isText ? 'Type the answer…' : 'Describe or reference the supporting evidence…'}
      style={textAreaStyle(p, font)}
    />
  );
}

export const textAreaStyle = (p: QPalette, font: string, minHeight = 88): React.CSSProperties => ({
  width: '100%', boxSizing: 'border-box', padding: '11px 13px', fontSize: 13.5, fontFamily: font, color: p.ink,
  background: p.surface2, border: `1px solid ${p.line}`, borderRadius: 8, outline: 'none', minHeight, resize: 'vertical',
});

// Only shown for a "No" / "Not applicable" answer. Optional -- the amber colour asks for a reason,
// it never blocks submitting.
export function ReasonField({ p, font, value, onChange, onBlur, onFocus }: {
  p: QPalette; font: string; value: string; onChange: (v: string) => void; onBlur?: () => void; onFocus: () => void;
}) {
  const empty = !value.trim();
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: empty ? p.amber : p.muted }}>Reason</span>
        <span style={{ fontSize: 11.5, color: p.faint }}>recommended for a No or Not applicable</span>
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        rows={2}
        placeholder="Compensating controls in place, planned remediation date, or why this doesn't apply"
        style={{ ...textAreaStyle(p, font, 60), fontSize: 13, borderColor: empty ? p.amberBdr : p.line }}
      />
    </div>
  );
}

// Header above the current section's questions, and the previous / next controls under them.
export function SectionHeader({ p, title, index, count, done, total }: {
  p: QPalette; title: string; index: number; count: number; done: number; total: number;
}) {
  const complete = total > 0 && done === total;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: p.muted, marginBottom: 3 }}>{index + 1} of {count}</div>
        <div style={{ fontSize: 19, fontWeight: 700, color: p.ink }}>{title}</div>
      </div>
      {total === 0
        ? <Chip p={p} tone="slate">Not shown yet</Chip>
        : <Chip p={p} tone={complete ? 'green' : done ? 'blue' : 'slate'}>{done} of {total} answered</Chip>}
    </div>
  );
}

// A section whose questions are all follow-ups of earlier answers (some questionnaires chain whole
// sections) -- explain why it's empty instead of showing a blank page.
export function EmptySection({ p }: { p: QPalette }) {
  return (
    <div style={{
      border: `1px dashed ${p.lineStrong}`, borderRadius: 12, padding: '28px 24px', textAlign: 'center',
      fontSize: 13.5, color: p.muted, lineHeight: 1.6, background: p.surface,
    }}>
      These questions appear once you answer the earlier ones they follow on from.
    </div>
  );
}

export function SectionPager({ p, font, prevTitle, nextTitle, onPrev, onNext, end }: {
  p: QPalette; font: string; prevTitle: string | null; nextTitle: string | null; onPrev: () => void; onNext: () => void; end?: React.ReactNode;
}) {
  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    fontFamily: font, cursor: 'pointer', border: `1px solid ${p.lineStrong}`, background: p.surface, color: p.ink,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20, paddingTop: 18, borderTop: `1px solid ${p.line}` }}>
      {prevTitle ? <button type="button" onClick={onPrev} style={btn}>← {prevTitle}</button> : <span />}
      {nextTitle ? (
        <button type="button" onClick={onNext} style={{ ...btn, background: p.blue, borderColor: p.blue, color: '#fff' }}>{nextTitle} →</button>
      ) : end || <span />}
    </div>
  );
}

// Which page-level section a question key lives in, and the code of its parent (for follow-ups),
// from the current tree -- used to jump from the sidebar to a question in another section.
export function indexTree<T extends QItem>(sections: { key: string; nodes: TreeNode<T>[] }[]) {
  const sectionOfKey: Record<string, string> = {};
  const parentOfKey: Record<string, string | null> = {};
  sections.forEach(s => flattenNodes(s.nodes).forEach(r => {
    sectionOfKey[r.item.question_key] = s.key;
    parentOfKey[r.item.question_key] = r.parentKey;
  }));
  return { sectionOfKey, parentOfKey };
}

/* ─── Page chrome ───────────────────────────────────────────────────── */

export function TopBar({ p, height, children }: { p: QPalette; height: number; children: React.ReactNode }) {
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 40, height, boxSizing: 'border-box', background: p.surface, borderBottom: `1px solid ${p.line}`,
      display: 'flex', alignItems: 'center', gap: 18, padding: '0 24px',
    }}>
      {children}
    </header>
  );
}

export function ProgressBlock({ p, done, total }: { p: QPalette; done: number; total: number }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div style={{ flexShrink: 0, textAlign: 'right' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: p.ink }}>{done} of {total} answered</div>
      <div style={{ width: 140, height: 5, borderRadius: 99, background: p.hover, marginTop: 5, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: pct === 100 ? p.green : p.blue, transition: 'width .2s' }} />
      </div>
    </div>
  );
}

// A search/filter result can't keep the follow-up tree (a matched follow-up's parent may not match),
// so it lists matches grouped by section, each follow-up still labelled with its parent.
export function FilteredList<T extends QItem>({ p, rows, sectionTitle, onClear, renderRow }: {
  p: QPalette; rows: FlatRow<T>[]; sectionTitle: (key: string) => string; onClear: () => void; renderRow: (r: FlatRow<T>) => React.ReactNode;
}) {
  const groups: { key: string; rows: FlatRow<T>[] }[] = [];
  rows.forEach(r => {
    const key = r.item.section || 'Other';
    let g = groups.find(x => x.key === key);
    if (!g) { g = { key, rows: [] }; groups.push(g); }
    g.rows.push(r);
  });
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: p.ink }}>{rows.length} matching question{rows.length === 1 ? '' : 's'}</div>
        <button type="button" onClick={onClear} style={{ background: 'none', border: 'none', color: p.blue, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
          Clear filter
        </button>
      </div>
      {groups.length ? groups.map(g => (
        <div key={g.key} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: p.muted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>{sectionTitle(g.key)}</div>
          {g.rows.map(r => renderRow(r))}
        </div>
      )) : (
        <div style={{ fontSize: 13, color: p.muted, textAlign: 'center', padding: '48px 0' }}>Nothing matches. Try another filter.</div>
      )}
    </>
  );
}
